import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import SignaturePad from "../components/SignaturePad";
import { CheckCircle2 } from "lucide-react";

/** Public page a kid opens from the signing link to read and sign their agreement. */
export default function SignAgreement() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [ticked, setTicked] = useState({});
  const [name, setName] = useState("");
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/agreement/${token}`)
      .then(({ data }) => { setDoc(data); setName(data.kid_name || ""); })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [token]);

  const allTicked = doc && doc.promises.every((_, i) => ticked[i]);
  const ready = allTicked && name.trim().length >= 2 && signature;

  const sign = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/agreement/${token}/sign`, { name, signature });
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F4F0] px-4 py-8 flex justify-center">
      <div className="w-full max-w-xl card-soft p-6 md:p-10 fade-in-up" data-testid="sign-agreement-page">
        {!doc && !error && <p className="text-[#707A6E]">Loading…</p>}
        {error && !doc && <p className="text-[#C06E52]">{error}</p>}
        {done && (
          <div className="text-center py-8">
            <CheckCircle2 size={48} className="mx-auto text-[#5C7A4F]" />
            <h1 className="font-heading text-3xl font-semibold mt-4 text-[#2C342A]">Signed. Thank you!</h1>
            <p className="text-[#707A6E] mt-2">Your screen time is unlocked. Your parents have a copy.</p>
          </div>
        )}
        {doc && !done && (
          <>
            <h1 className="font-heading text-3xl font-semibold text-[#2C342A]">{doc.title}</h1>
            <p className="mt-3 text-[#2C342A]">{doc.intro}</p>

            <h2 className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">I promise</h2>
            <ul className="mt-2 space-y-2">
              {doc.promises.map((p, i) => (
                <li key={i}>
                  <label className="flex items-start gap-3 rounded-xl border border-[#F0EFEB] bg-white px-4 py-3 cursor-pointer">
                    <input type="checkbox" className="mt-1 h-5 w-5" checked={!!ticked[i]}
                           onChange={(e) => setTicked((t) => ({ ...t, [i]: e.target.checked }))} />
                    <span className="text-[#2C342A]">{p}</span>
                  </label>
                </li>
              ))}
            </ul>

            {doc.consequences.length > 0 && (
              <>
                <h2 className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">If I break this agreement</h2>
                <ul className="mt-2 list-disc pl-6 space-y-1 text-[#2C342A]">
                  {doc.consequences.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </>
            )}

            <h2 className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Sign</h2>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                   className="mt-2 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3" />
            <div className="mt-3"><SignaturePad onChange={setSignature} /></div>
            {error && <p className="mt-3 text-sm text-[#C06E52]">{error}</p>}
            <button type="button" disabled={!ready || busy} onClick={sign}
                    className="mt-5 w-full rounded-xl bg-[#5C7A4F] px-5 py-4 font-medium text-white disabled:opacity-40"
                    data-testid="sign-agreement-btn">
              {busy ? "Signing…" : ready ? "I agree. Sign it" : "Tick every promise, then sign"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
